import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import {
  exposePdfExportMapReadiness,
  prepareMapForPdfExport
} from "../lib/pdf_export_helpers.mjs"

// Connects to data-controller="provider-search-map"
// Adds an interactive map view to provider search results
export default class extends Controller {
  static targets = ["map", "mapContainer", "resultsContainer", "expandIcon", "shrinkIcon"]
  static values = {
    accessToken: String,
    providers: Array,
    // { lat, lng } of the place searched around, for a map with no pin to show
    searchCenter: Object
  }

  // Zoom with pins to fit, and without: about the search radius around the searched place.
  static ZOOM_WITH_PINS = 10
  static ZOOM_WITHOUT_PINS = 9
  // Closest the map zooms when fitting pins: a few streets around the building. Several results
  // at one address (a hospital and its billing entity) have zero-size bounds, which fitBounds
  // would otherwise fill at Mapbox's max zoom of 22.
  static MAX_FIT_ZOOM = 15
  static US_CENTER = [-98.5795, 39.8283]
  static ZOOM_US = 3

  connect() {
    this.map = null
    this.markers = new Map() // provider result index -> marker
    this.locationGroups = new Map() // "lat,lng" -> [{ provider, index }]
    this.highlightedProviderIndexes = []
    this.visibleProviderIndexes = null
    this.visibleProviderIndexOrder = []
    this.visibleProviderPositions = new Map()
    this.expanded = false
    this.mapInitializationFrame = null
    this.mapInitialization = new Promise((resolve) => {
      this.resolveMapInitialization = resolve
    })
    this.removePdfExportMapReadiness = exposePdfExportMapReadiness(
      this.element,
      () => prepareMapForPdfExport(this.mapInitialization)
    )

    // Initialize map automatically if we have providers. The new pages' list view hides the map:
    // it's built when Map is first chosen (no map load for a member who never looks at it).
    this.mapDeferred = this.providersValue.length > 0 && this.element.classList.contains("search-results-v2") &&
      !this.element.classList.contains("search-results-v2--map")
    if (this.mapDeferred) {
      this.resolveMapInitialization(null)
    } else if (this.providersValue.length > 0) {
      // Small delay to ensure container is visible and sized
      this.mapInitializationFrame = requestAnimationFrame(() => {
        this.mapInitializationFrame = null
        this.initializeMap()
      })
    } else {
      this.resolveMapInitialization(null)
    }
  }

  initializeMap() {
    mapboxgl.accessToken = this.accessTokenValue

    try {
      const { center, zoom } = this.initialView()
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: 'mapbox://styles/mapbox/streets-v12',
        center,
        zoom,
        attributionControl: false,
        // Keep the WebGL buffer so the map can be snapshotted into the PDF export.
        preserveDrawingBuffer: true
      })
      this.map.addControl(new mapboxgl.NavigationControl(), 'top-left')

      this.map.on('load', () => {
        this.addMarkers()
        this.fitBoundsToProviders()
      })

      this.resolveMapInitialization(this.map)
      this.trackMetric("mapbox.search_map.load.success")
    } catch (error) {
      this.resolveMapInitialization(null)
      this.trackMetric("mapbox.search_map.load.error")
      console.error("Failed to initialize map:", error)
    }
  }

  addMarkers() {
    // Group providers by location (rounded to 4 decimal places ≈ 11m precision)
    this.locationGroups = new Map()

    this.providersValue.forEach((provider, index) => {
      if (!provider.latitude || !provider.longitude) return
      if (provider.any_geo_location) return

      const key = `${Number(provider.latitude).toFixed(4)},${Number(provider.longitude).toFixed(4)}`
      if (!this.locationGroups.has(key)) {
        this.locationGroups.set(key, [])
      }
      this.locationGroups.get(key).push({ provider, index })
    })

    // Create markers for each location group
    this.locationGroups.forEach((group) => {
      if (group.length === 1) {
        // Single provider — numbered marker
        this.createSingleMarker(group[0].provider, group[0].index)
      } else {
        // Multiple providers at same location — stacked numbered markers
        // Iterate in reverse so the first result ends up on top (highest z-index)
        for (let i = group.length - 1; i >= 0; i--) {
          const offset = [i * 4, i * 4]
          this.createSingleMarker(group[i].provider, group[i].index, offset)
        }
      }
    })
  }

  createSingleMarker(provider, index, offset = null) {
    const el = document.createElement('div')
    el.className = 'provider-map-marker'
    el.dataset.npi = provider.npi
    el.dataset.providerResultIndex = index
    el.textContent = index + 1

    const markerOptions = offset ? { offset } : {}
    const marker = new mapboxgl.Marker(el, markerOptions)
      .setLngLat([provider.longitude, provider.latitude])
      .addTo(this.map)

    if (this.visibleProviderIndexes) {
      const resultIndex = index.toString()
      const visibleIndex = this.visibleProviderPositions.get(resultIndex)
      el.style.display = visibleIndex === undefined ? "none" : ""
      if (visibleIndex !== undefined) el.textContent = visibleIndex + 1
    }

    el.addEventListener('click', (e) => {
      e.stopPropagation()
      this.highlightProviders([index])
    })

    // Marker hover → subtle card highlight (no-op while the card's page is
    // still unloaded; a hover shouldn't force result pages to load)
    el.addEventListener('mouseenter', () => {
      const card = this.resultCardFor(index)
      if (card) card.classList.add('provider-card-hover-highlighted')
    })
    el.addEventListener('mouseleave', () => {
      const card = this.resultCardFor(index)
      if (card) card.classList.remove('provider-card-hover-highlighted')
    })

    this.markers.set(index.toString(), marker)
  }

  filterProviders(event) {
    this.visibleProviderIndexOrder = (event.detail?.visibleProviderIndexes || []).map(String)
    this.visibleProviderIndexes = new Set(this.visibleProviderIndexOrder)
    this.visibleProviderPositions = new Map(
      this.visibleProviderIndexOrder.map((resultIndex, visibleIndex) => [resultIndex, visibleIndex])
    )

    this.element.querySelectorAll(".provider-map-marker").forEach((element) => {
      const visibleIndex = this.visibleProviderPositions.get(element.dataset.providerResultIndex)
      element.style.display = visibleIndex === undefined ? "none" : ""
      if (visibleIndex !== undefined) element.textContent = visibleIndex + 1
    })

    if (this.map) this.fitBoundsToProviders()
  }

  // --- Highlight system ---

  clearHighlights() {
    this.element.querySelectorAll('.provider-card-highlighted').forEach(el => {
      el.classList.remove('provider-card-highlighted')
    })
    this.element.querySelectorAll('.provider-card-hover-highlighted').forEach(el => {
      el.classList.remove('provider-card-hover-highlighted')
    })
    this.element.querySelectorAll('.marker-highlighted').forEach(el => {
      el.classList.remove('marker-highlighted')
    })
    this.element.querySelectorAll('.marker-hover-highlighted').forEach(el => {
      el.classList.remove('marker-hover-highlighted')
    })
    this.highlightedProviderIndexes = []
  }

  // Markers cover the full result set while cards load lazily a page at a
  // time, so a marker can target a card that isn't in the DOM yet — load the
  // remaining pages first, then highlight and scroll.
  highlightProviders(providerIndexes) {
    return this.ensureCardsLoaded(providerIndexes).then(() => this.applyHighlights(providerIndexes))
  }

  applyHighlights(providerIndexes) {
    this.clearHighlights()
    this.highlightedProviderIndexes = providerIndexes.map(String)

    this.highlightedProviderIndexes.forEach(providerIndex => {
      const card = this.resultCardFor(providerIndex)
      const marker = this.markers.get(providerIndex)

      if (card) card.classList.add('provider-card-highlighted')
      if (marker) marker.getElement().classList.add('marker-highlighted')
    })

    // Scroll to the first highlighted card
    const firstCard = this.resultCardFor(this.highlightedProviderIndexes[0])
    if (firstCard) {
      firstCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  // Map markers carry the same data attribute as result cards, so card
  // lookups must exclude them.
  resultCardFor(providerIndex) {
    return this.element.querySelector(
      `[data-provider-result-index="${providerIndex}"]:not(.provider-map-marker)`
    )
  }

  // Asks the provider-search-pagination controller (same contract as
  // pdf-export:prepare) to eagerly load the remaining result pages when any
  // requested card is missing. Resolves even if a page fails to load so the
  // interaction still highlights whatever is present.
  ensureCardsLoaded(providerIndexes) {
    if (providerIndexes.every((index) => this.resultCardFor(index))) return Promise.resolve()

    const detail = { pending: [], target: this.element }
    window.dispatchEvent(new CustomEvent("provider-search-map:load-cards", { detail }))
    return Promise.allSettled(detail.pending)
  }

  // --- Card interactions ---

  cardClicked(event) {
    // Don't handle clicks on interactive elements (buttons, links, the office list) within the card
    if (event.target.closest('a, button, details')) return

    // The new pages: a card opens its provider page, not a drawer.
    const pageUrl = event.currentTarget.dataset.pageUrl
    if (pageUrl) {
      window.Turbo.visit(pageUrl)
      return
    }

    const detailsUrl = event.currentTarget.dataset.detailsUrl
    if (detailsUrl) {
      const drawerEvent = new Event('drawer.open')
      drawerEvent.url = detailsUrl
      document.dispatchEvent(drawerEvent)
    }
  }

  // The new pages' List/Map switch (providers/search_results): the list alone, or the map beside it.
  showList() {
    this.setView("list")
  }

  showMap() {
    this.setView("map")
  }

  setView(view) {
    // An expanded map can't stay full-width behind a list that hides it.
    if (view === "list" && this.expanded) this.collapseView()
    this.element.classList.toggle("search-results-v2--map", view === "map")
    this.element.querySelectorAll("[data-results-view]").forEach(button => {
      button.setAttribute("aria-pressed", String(button.dataset.resultsView === view))
    })
    if (view === "map" && this.mapDeferred) {
      this.mapDeferred = false
      requestAnimationFrame(() => this.initializeMap())
      return
    }
    if (view === "map" && this.map) {
      requestAnimationFrame(() => {
        this.map.resize()
        this.fitBoundsToProviders()
      })
    }
  }

  cardHovered(event) {
    const providerIndex = event.currentTarget.dataset.providerResultIndex
    if (providerIndex === undefined) return

    const marker = this.markers.get(providerIndex)
    if (marker) {
      marker.getElement().classList.add('marker-hover-highlighted')
    }
  }

  cardUnhovered(event) {
    const providerIndex = event.currentTarget.dataset.providerResultIndex
    if (providerIndex === undefined) return

    const marker = this.markers.get(providerIndex)
    if (marker) {
      marker.getElement().classList.remove('marker-hover-highlighted')
    }
  }

  // --- Expand / Collapse ---

  toggleExpand() {
    if (this.expanded) {
      this.collapseView()
    } else {
      this.expandView()
    }
  }

  expandView() {
    this.expanded = true

    document.dispatchEvent(new CustomEvent('drawer.expand'))

    // Switch layout from vertical to horizontal
    const layout = this.element.querySelector('.map-view-layout')
    if (layout) {
      layout.classList.remove('flex-col')
      layout.classList.add('flex-row', 'map-view-expanded')
    }

    if (this.hasExpandIconTarget) {
      this.expandIconTarget.classList.add('hidden')
    }
    if (this.hasShrinkIconTarget) {
      this.shrinkIconTarget.classList.remove('hidden')
    }

    // Resize map after drawer transition completes (drawer uses duration-500 = 500ms)
    this.resizeMapAfterTransition()
  }

  collapseView() {
    this.expanded = false

    document.dispatchEvent(new CustomEvent('drawer.collapse'))

    const layout = this.element.querySelector('.map-view-layout')
    if (layout) {
      layout.classList.add('flex-col')
      layout.classList.remove('flex-row', 'map-view-expanded')
    }

    if (this.hasExpandIconTarget) {
      this.expandIconTarget.classList.remove('hidden')
    }
    if (this.hasShrinkIconTarget) {
      this.shrinkIconTarget.classList.add('hidden')
    }

    this.resizeMapAfterTransition()
  }

  // Drawer panel uses CSS `duration-500` (500ms); wait slightly longer to resize the map
  static DRAWER_TRANSITION_MS = 550

  resizeMapAfterTransition() {
    setTimeout(() => {
      if (this.map) {
        this.map.resize()
        this.fitBoundsToProviders()
      }
    }, this.constructor.DRAWER_TRANSITION_MS)
  }

  // --- Map utilities ---

  // Where the map opens: around the pins, or, when no result has one (a provider looked
  // up by name whose address was never placed), around the place searched.
  initialView() {
    const view = this.constructor
    if (this.pinnedProviders().length > 0) return { center: this.calculateCenter(), zoom: view.ZOOM_WITH_PINS }

    const { lat, lng } = this.searchCenterValue || {}
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { center: [lng, lat], zoom: view.ZOOM_WITHOUT_PINS }

    return { center: view.US_CENTER, zoom: view.ZOOM_US }
  }

  pinnedProviders() {
    return this.visibleProviders().filter(p => p.latitude && p.longitude && !p.any_geo_location)
  }

  fitBoundsToProviders() {
    const providersWithCoords = this.pinnedProviders()
    if (providersWithCoords.length === 0) return

    if (providersWithCoords.length === 1) {
      const p = providersWithCoords[0]
      this.map.flyTo({
        center: [p.longitude, p.latitude],
        zoom: this.constructor.MAX_FIT_ZOOM
      })
      return
    }

    const bounds = new mapboxgl.LngLatBounds()
    providersWithCoords.forEach((provider) => {
      bounds.extend([provider.longitude, provider.latitude])
    })

    this.map.fitBounds(bounds, { padding: 50, maxZoom: this.constructor.MAX_FIT_ZOOM })
  }

  calculateCenter() {
    const providers = this.pinnedProviders()
    if (providers.length === 0) return this.constructor.US_CENTER

    const avgLat = providers.reduce((sum, p) => sum + p.latitude, 0) / providers.length
    const avgLng = providers.reduce((sum, p) => sum + p.longitude, 0) / providers.length

    return [avgLng, avgLat]
  }

  visibleProviders() {
    if (!this.visibleProviderIndexes) return this.providersValue

    return this.providersValue.filter((_provider, index) => this.visibleProviderIndexes.has(index.toString()))
  }

  trackMetric(name) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(`/api/v1/metrics/increment_counter?name=${name}`, {
      method: "POST",
      headers: csrfToken ? { "X-CSRF-Token": csrfToken } : {}
    }).catch(() => {
      // Silently ignore metric failures
    })
  }

  disconnect() {
    if (this.mapInitializationFrame != null) {
      cancelAnimationFrame(this.mapInitializationFrame)
      this.mapInitializationFrame = null
    }
    this.resolveMapInitialization?.(null)
    this.removePdfExportMapReadiness?.()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
    this.markers.clear()
    this.locationGroups.clear()
  }
}
